/**
 * TransientStayDetailPanel — Detail view for a transient stay.
 *
 * Supports two data paths:
 * - Direct `stay` prop (from TransientStaysPage table)
 * - `stayId` prop (fetches via useTransientStayById — from ramp card)
 *
 * Renders as Dialog (modal) or Sheet (sidebar) based on store preference.
 * Hosts lifecycle dialogs: Cancel, CheckOut, NoShow.
 */

import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  PanelRight,
  Maximize2,
  Pencil,
  Building2,
  User,
  Plane,
  Calendar,
  DollarSign,
  AlertTriangle,
  LogIn,
  LogOut,
  StickyNote,
  Ban,
  UserX,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTransientViewStore, type DetailPanelMode } from '@/stores/transientViewStore'
import { useTransientStayById, useCancelTransientStay, useCheckOutTransientStay, useNoShowTransientStay, useTerminateTransientStay } from '@/hooks/useTransientStays'
import { TransientStayMutateDrawer } from './TransientStayMutateDrawer'
import { CancelTransientStayDialog } from './CancelTransientStayDialog'
import { NoShowTransientStayDialog } from './NoShowTransientStayDialog'
import { CheckOutTransientStayDialog } from './CheckOutTransientStayDialog'
import { TerminateTransientStayDialog } from './TerminateTransientStayDialog'
import type { TransientStayTableRow } from '@/types/transient-stays'
import { TRANSIENT_STATUS_LABELS, TRANSIENT_STATUS_VARIANTS, BOOKING_TYPE_LABELS, BOOKING_SOURCE_LABELS, RATE_TYPE_LABELS, formatCurrency, formatDateTime, canCancelTransient, canCheckOutTransient, canNoShowTransient, canCheckInTransient, canTerminateTransient } from '@/types/transient-stays'

// ============================================
// Types
// ============================================

interface TransientStayDetailPanelProps {
  /** Direct stay data (from table row click) */
  stay?: TransientStayTableRow | null
  /** Stay ID for fetch mode (from ramp card) */
  stayId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onEdit?: (id: string) => void
  onCheckIn?: (id: string) => void
  onCheckOut?: (id: string) => void
}

// ============================================
// Warning Banner System
// ============================================

type WarningSeverity = 'critical' | 'warning' | 'notice'

interface WarningBanner {
  severity: WarningSeverity
  message: string
}

function computeWarnings(stay: TransientStayTableRow): WarningBanner[] {
  const warnings: WarningBanner[] = []

  // Overstaying: checked_in + past expected checkout + no actual checkout
  if (
    stay.status === 'checked_in' &&
    stay.expected_check_out &&
    !stay.actual_check_out
  ) {
    const now = new Date()
    const expected = new Date(stay.expected_check_out)
    if (now > expected) {
      const hours = Math.round(
        (now.getTime() - expected.getTime()) / (1000 * 60 * 60)
      )
      warnings.push({
        severity: 'critical',
        message: `Overstaying — ${hours}h past expected checkout`,
      })
    }
  }

  // No unit assigned
  if (!stay.unit_id) {
    warnings.push({
      severity: 'warning',
      message: 'No space assigned — needs allocation',
    })
  }

  // Open-ended stay
  if (stay.status === 'checked_in' && !stay.expected_check_out) {
    warnings.push({
      severity: 'notice',
      message: 'Open-ended stay — no departure date set',
    })
  }

  // Cancelled
  if (stay.status === 'cancelled') {
    warnings.push({
      severity: 'notice',
      message: stay.cancellation_reason
        ? `Cancelled — ${stay.cancellation_reason}`
        : 'Booking cancelled',
    })
  }

  // No-show
  if (stay.status === 'no_show') {
    warnings.push({ severity: 'notice', message: 'Guest did not arrive — no-show' })
  }

  return warnings
}

const SEVERITY_STYLES: Record<WarningSeverity, string> = {
  critical:
    'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200',
  warning:
    'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200',
  notice:
    'border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950/30 dark:text-yellow-200',
}

// ============================================
// Detail Content (Pure Presentation)
// ============================================

function DetailContent({
  stay,
  onEdit,
  onCheckIn,
  onCheckOut,
  onCancel,
  onNoShow,
  onTerminate,
  panelMode,
  onToggleMode,
}: {
  stay: TransientStayTableRow
  onEdit: (id: string) => void
  onCheckIn?: (id: string) => void
  onCheckOut?: (id: string) => void
  onCancel: () => void
  onNoShow: () => void
  onTerminate: () => void
  panelMode: DetailPanelMode
  onToggleMode: () => void
}) {
  const nightNumber =
    stay.status === 'checked_in' ? getNightNumber(stay.check_in) : null
  const warnings = computeWarnings(stay)

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg font-bold">
              {stay.booking_number}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={TRANSIENT_STATUS_VARIANTS[stay.status]}>
              {TRANSIENT_STATUS_LABELS[stay.status]}
            </Badge>
            <Badge variant="outline">
              {BOOKING_TYPE_LABELS[stay.booking_type]}
            </Badge>
            {nightNumber && stay.nights_count && (
              <span className="text-sm text-muted-foreground">
                Day {nightNumber} of {stay.nights_count}
              </span>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleMode}
          className="h-8 w-8"
        >
          {panelMode === 'sidePanel' ? (
            <Maximize2 className="h-4 w-4" />
          ) : (
            <PanelRight className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Warning Banners */}
      {warnings.map((w, i) => (
        <div
          key={i}
          className={`rounded-lg border p-3 ${SEVERITY_STYLES[w.severity]}`}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">{w.message}</span>
          </div>
        </div>
      ))}

      <Separator />

      {/* Client Section */}
      <Section icon={User} title="Client">
        <DetailRow label="Name" value={stay.client_name || 'Walk-in'} />
      </Section>

      {/* Aircraft Section */}
      <Section icon={Plane} title="Aircraft">
        <DetailRow
          label="Tail #"
          value={stay.tail_number}
          valueClass="font-mono font-medium"
        />
        <DetailRow label="Type" value={stay.aircraft_type_name} />
        {stay.wingspan && (
          <DetailRow label="Wingspan" value={`${stay.wingspan} ft`} />
        )}
        {stay.length && (
          <DetailRow label="Length" value={`${stay.length} ft`} />
        )}
      </Section>

      {/* Space Section */}
      <Section icon={Building2} title="Space">
        <DetailRow label="Unit" value={stay.unit_code ?? 'Not assigned'} />
        <DetailRow label="Building" value={stay.building_name} />
        <DetailRow label="Location" value={stay.location_name} />
      </Section>

      {/* Stay Details */}
      <Section icon={Calendar} title="Stay Details">
        <DetailRow label="Arrived" value={formatDateTime(stay.check_in)} />
        <DetailRow
          label="Departing"
          value={
            stay.actual_check_out
              ? formatDateTime(stay.actual_check_out)
              : stay.expected_check_out
                ? formatDateTime(stay.expected_check_out)
                : 'Open-ended'
          }
        />
        <DetailRow
          label="Duration"
          value={
            stay.nights_count
              ? `${stay.nights_count} night${stay.nights_count > 1 ? 's' : ''}`
              : 'TBD'
          }
        />
        <DetailRow
          label="Booking Type"
          value={BOOKING_TYPE_LABELS[stay.booking_type]}
        />
        {stay.source && (
          <DetailRow
            label="Source"
            value={BOOKING_SOURCE_LABELS[stay.source]}
          />
        )}
      </Section>

      {/* Billing Section */}
      <Section icon={DollarSign} title="Billing">
        {stay.rate_amount != null && (
          <DetailRow
            label="Rate"
            value={`${formatCurrency(stay.rate_amount)}${stay.rate_type ? ` (${RATE_TYPE_LABELS[stay.rate_type]})` : ''}`}
          />
        )}
        {stay.subtotal != null && (
          <DetailRow label="Subtotal" value={formatCurrency(stay.subtotal)} />
        )}
        {stay.discount_amount != null && stay.discount_amount > 0 && (
          <DetailRow
            label="Discount"
            value={`-${formatCurrency(stay.discount_amount)}${stay.discount_reason ? ` (${stay.discount_reason})` : ''}`}
          />
        )}
        {stay.tax_amount != null && stay.tax_amount > 0 && (
          <DetailRow label="Tax" value={formatCurrency(stay.tax_amount)} />
        )}
        <DetailRow
          label="Total"
          value={formatCurrency(stay.total_amount)}
          valueClass="font-medium"
        />
      </Section>

      {/* Notes Section */}
      {(stay.notes || stay.special_requests) && (
        <Section icon={StickyNote} title="Notes">
          {stay.notes && <DetailRow label="Notes" value={stay.notes} />}
          {stay.special_requests && (
            <DetailRow
              label="Special Requests"
              value={stay.special_requests}
            />
          )}
        </Section>
      )}

      {/* Cancellation Details */}
      {stay.status === 'cancelled' && stay.cancellation_reason && (
        <>
          <Separator />
          <Section icon={XCircle} title="Cancellation Details">
            <DetailRow label="Reason" value={stay.cancellation_reason} />
          </Section>
        </>
      )}

      <Separator />

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => onEdit(stay.id)}
          className="flex-1"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </Button>
        {canCheckInTransient(stay.status) && onCheckIn && (
          <Button
            variant="default"
            onClick={() => onCheckIn(stay.id)}
            className="flex-1"
          >
            <LogIn className="mr-2 h-4 w-4" />
            Check In
          </Button>
        )}
        {canCheckOutTransient(stay.status) && onCheckOut && (
          <Button
            variant="default"
            onClick={() => onCheckOut(stay.id)}
            className="flex-1"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Check Out
          </Button>
        )}
        {canCancelTransient(stay.status) && (
          <Button
            variant="destructive"
            onClick={onCancel}
            className="flex-1"
          >
            <Ban className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        )}
        {canNoShowTransient(stay.status) && (
          <Button
            variant="outline"
            onClick={onNoShow}
            className="flex-1 text-destructive border-destructive/30 hover:bg-destructive/10"
          >
            <UserX className="mr-2 h-4 w-4" />
            No-Show
          </Button>
        )}
        {canTerminateTransient(stay.status) && (
          <Button
            variant="destructive"
            onClick={onTerminate}
            className="flex-1"
          >
            <XCircle className="mr-2 h-4 w-4" />
            Terminate
          </Button>
        )}
      </div>
    </>
  )
}

// ============================================
// Main Panel Component
// ============================================

export function TransientStayDetailPanel({
  stay: stayProp,
  stayId,
  open,
  onOpenChange,
  onEdit: onEditProp,
  onCheckIn,
  onCheckOut: onCheckOutProp,
}: TransientStayDetailPanelProps) {
  const { detailPanelMode, setDetailPanelMode } = useTransientViewStore()

  // Fetch by ID if no direct stay prop
  const { data: fetchedStay, isLoading } = useTransientStayById(
    !stayProp && stayId ? stayId : undefined
  )
  const resolvedStay = stayProp ?? fetchedStay ?? null

  // Lifecycle dialogs
  const [cancelOpen, setCancelOpen] = useState(false)
  const [noShowOpen, setNoShowOpen] = useState(false)
  const [checkOutOpen, setCheckOutOpen] = useState(false)
  const [terminateOpen, setTerminateOpen] = useState(false)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)

  const cancelMutation = useCancelTransientStay()
  const checkOutMutation = useCheckOutTransientStay()
  const noShowMutation = useNoShowTransientStay()
  const terminateMutation = useTerminateTransientStay()

  const handleToggleMode = () => {
    setDetailPanelMode(
      detailPanelMode === 'sidePanel' ? 'modal' : 'sidePanel'
    )
  }

  const handleEdit = (id: string) => {
    if (onEditProp) {
      onEditProp(id)
    } else {
      setEditDrawerOpen(true)
    }
  }

  const handleCheckOut = (id: string) => {
    if (onCheckOutProp) {
      onCheckOutProp(id)
    } else {
      setCheckOutOpen(true)
    }
  }

  const confirmCancel = (params: { cancellation_reason?: string }) => {
    if (!resolvedStay) return
    cancelMutation.mutate(
      { id: resolvedStay.id, ...params },
      {
        onSuccess: () => {
          setCancelOpen(false)
          toast.success(`Booking ${resolvedStay.booking_number} cancelled`)
        },
      }
    )
  }

  const confirmCheckOut = () => {
    if (!resolvedStay) return
    checkOutMutation.mutate(resolvedStay.id, {
      onSuccess: () => {
        setCheckOutOpen(false)
        toast.success(`Booking ${resolvedStay.booking_number} checked out`)
      },
    })
  }

  const confirmNoShow = () => {
    if (!resolvedStay) return
    noShowMutation.mutate(resolvedStay.id, {
      onSuccess: () => {
        setNoShowOpen(false)
        toast.success(`Booking ${resolvedStay.booking_number} marked as no-show`)
      },
    })
  }

  const confirmTerminate = (params: { termination_reason?: string }) => {
    if (!resolvedStay) return
    terminateMutation.mutate(
      { id: resolvedStay.id, ...params },
      {
        onSuccess: () => {
          setTerminateOpen(false)
          toast.success(`Booking ${resolvedStay.booking_number} terminated`)
        },
      }
    )
  }

  if (!open) return null

  // Loading state
  if (isLoading && !resolvedStay) {
    const skeleton = (
      <div className="flex items-center justify-center h-32">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )

    if (detailPanelMode === 'modal') {
      return (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-lg max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="sr-only">Loading</DialogTitle>
            </DialogHeader>
            {skeleton}
          </DialogContent>
        </Dialog>
      )
    }
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-md p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Loading</SheetTitle>
          </SheetHeader>
          {skeleton}
        </SheetContent>
      </Sheet>
    )
  }

  if (!resolvedStay) return null

  const content = (
    <DetailContent
      stay={resolvedStay}
      onEdit={handleEdit}
      onCheckIn={onCheckIn}
      onCheckOut={handleCheckOut}
      onCancel={() => setCancelOpen(true)}
      onNoShow={() => setNoShowOpen(true)}
      onTerminate={() => setTerminateOpen(true)}
      panelMode={detailPanelMode}
      onToggleMode={handleToggleMode}
    />
  )

  const dialogs = (
    <>
      {/* Cancel Dialog */}
      <CancelTransientStayDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        bookingNumber={resolvedStay.booking_number}
        onConfirm={confirmCancel}
        isPending={cancelMutation.isPending}
      />

      {/* No-Show Dialog */}
      <NoShowTransientStayDialog
        open={noShowOpen}
        onOpenChange={setNoShowOpen}
        bookingNumber={resolvedStay.booking_number}
        onConfirm={confirmNoShow}
        isPending={noShowMutation.isPending}
      />

      {/* Check Out Dialog */}
      <CheckOutTransientStayDialog
        open={checkOutOpen}
        onOpenChange={setCheckOutOpen}
        bookingNumber={resolvedStay.booking_number}
        onConfirm={confirmCheckOut}
        isPending={checkOutMutation.isPending}
      />

      {/* Terminate Dialog */}
      <TerminateTransientStayDialog
        open={terminateOpen}
        onOpenChange={setTerminateOpen}
        bookingNumber={resolvedStay.booking_number}
        onConfirm={confirmTerminate}
        isPending={terminateMutation.isPending}
      />

      {/* Edit Drawer */}
      <TransientStayMutateDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        transientStayId={resolvedStay.id}
      />
    </>
  )

  if (detailPanelMode === 'modal') {
    return (
      <>
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="sr-only">
                Transient Stay Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">{content}</div>
          </DialogContent>
        </Dialog>
        {dialogs}
      </>
    )
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-md p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Transient Stay Details</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-full">
            <div className="space-y-4 p-6">{content}</div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      {dialogs}
    </>
  )
}

// ============================================
// Helpers
// ============================================

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {title}
      </div>
      <div className="space-y-1 pl-6">{children}</div>
    </div>
  )
}

function DetailRow({
  label,
  value,
  valueClass,
}: {
  label: string
  value: string | number | null | undefined
  valueClass?: string
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={valueClass}>{value ?? '—'}</span>
    </div>
  )
}

function getNightNumber(checkIn: string): number {
  const start = new Date(checkIn)
  const now = new Date()
  const diffTime = now.getTime() - start.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(1, diffDays + 1)
}
